import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const AnalyticsChart = ({ pageId }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const chartRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        fetch(`/api/analytics/${pageId}`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to load analytics');
                return res.json();
            })
            .then(setData)
            .catch(err => setError(err.message));
    }, [pageId]);

    useEffect(() => {
        if (!data || !canvasRef.current) return;

        if (chartRef.current) {
            chartRef.current.destroy();
        }

        const labels = data.dailyStats.map(d => {
            const date = new Date(d.date);
            return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
        });

        chartRef.current = new Chart(canvasRef.current, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Sidvisningar',
                        data: data.dailyStats.map(d => d.views),
                        borderColor: '#4A7C9B',
                        backgroundColor: 'rgba(74, 124, 155, 0.1)',
                        fill: true,
                        tension: 0.3,
                    },
                    {
                        label: 'Webbplatsklick',
                        data: data.dailyStats.map(d => d.webpageClicks),
                        borderColor: '#E8945A',
                        backgroundColor: 'rgba(232, 148, 90, 0.1)',
                        fill: false,
                        tension: 0.3,
                    },
                    {
                        label: 'E-postklick',
                        data: data.dailyStats.map(d => d.emailClicks),
                        borderColor: '#4A9E6B',
                        backgroundColor: 'rgba(74, 158, 107, 0.1)',
                        fill: false,
                        tension: 0.3,
                    },
                    {
                        label: 'Telefonklick',
                        data: data.dailyStats.map(d => d.phoneClicks),
                        borderColor: '#9B6CA7',
                        backgroundColor: 'rgba(155, 108, 167, 0.1)',
                        fill: false,
                        tension: 0.3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom',
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: { precision: 0 },
                    },
                },
            },
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [data]);

    if (error) return <p>Kunde inte ladda statistik.</p>;
    if (!data) return <p>Laddar statistik...</p>;

    return (
        <div>
            <div className="analytics-summary">
                <div className="analytics-card">
                    <div className="analytics-card__value">{data.totalViews}</div>
                    <div className="analytics-card__label">Totala visningar</div>
                </div>
                <div className="analytics-card">
                    <div className="analytics-card__value">{data.monthViews}</div>
                    <div className="analytics-card__label">Visningar (30 dagar)</div>
                </div>
                <div className="analytics-card">
                    <div className="analytics-card__value">{data.monthClicks}</div>
                    <div className="analytics-card__label">Klick (30 dagar)</div>
                </div>
            </div>
            <div style={{ height: '300px' }}>
                <canvas ref={canvasRef}></canvas>
            </div>
        </div>
    );
};

window.createAnalyticsChart = (containerId, pageId) => {
    const container = document.getElementById(containerId);
    if (container) {
        const root = ReactDOM.createRoot(container);
        root.render(<AnalyticsChart pageId={pageId} />);
    }
};

export default AnalyticsChart;
